import { ChangeDetectionHost } from './change-detection.types';

type ChangeDetectionCompareOptions = {
  pane?: 'left' | 'right';
  compareKey?: string;
  sensorType?: string;
};

export class ChangeDetectionController {
  constructor(private readonly host: ChangeDetectionHost) {}

  isChangeDetectionTask(taskStrategy: any): boolean {
    return `${taskStrategy ?? ''}`.trim().toLowerCase() === 'change-detection';
  }

  getDynamicTabNames(sensor: any): string[] {
    if (
      typeof sensor?.currentSensorData === 'object' &&
      sensor?.currentSensorData !== null
    ) {
      return Object.keys(sensor.currentSensorData).filter(
        (key) =>
          Array.isArray(sensor.currentSensorData[key]) &&
          sensor.currentSensorData[key].length > 0
      );
    }
    return [];
  }

  selectChangeDetectionTab(
    collection: any,
    collectionIndex: number,
    sensorIndex: number,
    sensor: any,
    tabName: string
  ): void {
    this.selectChangeDetectionTabInternal(
      collection,
      collectionIndex,
      sensorIndex,
      sensor,
      tabName,
      true
    );
  }

  forceSelectChangeDetectionTab(
    collection: any,
    collectionIndex: number,
    sensorIndex: number,
    sensor: any,
    tabName: string
  ): void {
    this.selectChangeDetectionTabInternal(
      collection,
      collectionIndex,
      sensorIndex,
      sensor,
      tabName,
      false
    );
  }

  private selectChangeDetectionTabInternal(
    collection: any,
    collectionIndex: number,
    sensorIndex: number,
    sensor: any,
    tabName: string,
    allowToggleOff: boolean
  ): void {
    const targetSensor = collection?.sensors?.[sensorIndex] ?? sensor;
    if (!targetSensor) {
      return;
    }

    const selectedTabs = Array.isArray(this.host.selectedChangeDetectionTab)
      ? this.host.selectedChangeDetectionTab
      : [];
    const isSelected = selectedTabs.includes(tabName);

    if (allowToggleOff && isSelected) {
      this.host.selectedChangeDetectionTab = selectedTabs.filter(
        (selectedTab) => selectedTab !== tabName
      );
      this.host.clearChangeDetectionTabLayers(tabName);
      return;
    }

    const sceneData = this.getTabSceneData(targetSensor, tabName);
    if (sceneData.length === 0) {
      return;
    }

    this.host.selectedChangeDetectionTab = isSelected
      ? selectedTabs
      : [...selectedTabs, tabName];
    if (!allowToggleOff && isSelected) {
      this.host.clearChangeDetectionTabLayers(tabName);
    }
    this.loadChangeDetectionScenes(collection, targetSensor, sceneData, tabName);
  }

  loadChangeDetectionRowData(
    collection: any,
    sensor: any,
    sceneId: string,
    sceneData?: any,
    options?: ChangeDetectionCompareOptions
  ): void {
    const sceneTarget = this.resolveChangeDetectionSceneTarget(
      sensor,
      collection,
      sceneId,
      sceneData
    );

    if (!sceneTarget?.scene || !sceneTarget?.sensorId) {
      return;
    }

    const { scene, sensorId } = sceneTarget;
    if (!scene?.id || scene?.status === 'failed' || !scene?.footprint) {
      return;
    }

    const shouldLoadTiff =
      this.host.loadTiff ||
      this.host.imageType === 'tif' ||
      this.host.imageType === 'tiff';

    if (options?.pane) {
      this.host.mapPane = options.pane;
      scene.pane = options.pane;
    }
    if (options?.compareKey) {
      scene.start = options.compareKey;
      scene.changeDetectionCompareKey = options.compareKey;
      scene.isChangeDetectionHistoryCompare = true;
    }
    if (options?.sensorType) {
      scene.isChangeDetectionCurrentWeekTab = true;
      scene.currentWeekSensorType = options.sensorType;
    }

    scene.checked = true;
    scene.imageType = shouldLoadTiff ? 'tif' : 'png';

    const sensorObj = shouldLoadTiff
      ? {
          sensorId,
          sceneId: scene.id,
          action: 'sensor',
          fileType: scene.imageType,
          ...(options?.sensorType ? { sensorType: options.sensorType } : {}),
        }
      : {
          sensorId,
          sceneId: scene.id,
          action: 'sensor',
          type: scene.imageType,
          ...(options?.sensorType ? { sensorType: options.sensorType } : {}),
        };

    this.host.tifPopupShown = 0;
    this.host.fetchImageFromServer(collection, sensorObj, scene);
  }

  private getTabSceneData(sensor: any, tabName: string): any[] {
    const data = sensor?.currentSensorData?.[tabName];
    return Array.isArray(data) ? data : [];
  }

  private getChangeDetectionSceneId(
    sceneData: any,
    baseScene?: any
  ): string | null {
    const rawScene =
      sceneData?.scene && typeof sceneData.scene === 'object'
        ? sceneData.scene
        : sceneData;

    return (
      rawScene?.id ??
      rawScene?.sceneId ??
      rawScene?.scene_id ??
      sceneData?.sceneId ??
      sceneData?.id ??
      sceneData?.scene?.id ??
      baseScene?.id ??
      null
    );
  }

  private normalizeChangeDetectionScene(
    sceneData: any,
    baseScene?: any,
    sensor?: any
  ): any | null {
    const rawScene =
      sceneData?.scene && typeof sceneData.scene === 'object'
        ? sceneData.scene
        : sceneData;

    if (!rawScene && !baseScene) {
      return null;
    }

    const sceneId = this.getChangeDetectionSceneId(sceneData, baseScene);

    if (!sceneId) {
      return null;
    }

    const predictedDate =
      rawScene?.predictedDate ??
      rawScene?.predicted_date ??
      baseScene?.predictedDate ??
      baseScene?.predicted_date;

    return {
      ...(baseScene ?? {}),
      ...(rawScene ?? {}),
      id: sceneId,
      scene_id: rawScene?.scene_id ?? baseScene?.scene_id,
      sceneName:
        rawScene?.sceneName ?? rawScene?.name ?? baseScene?.sceneName,
      name: rawScene?.name ?? baseScene?.name,
      predictedDate,
      predicted_date:
        rawScene?.predicted_date ??
        baseScene?.predicted_date ??
        predictedDate,
      footprint: rawScene?.footprint ?? baseScene?.footprint,
      tif_footprint: rawScene?.tif_footprint ?? baseScene?.tif_footprint,
      png_footprint: rawScene?.png_footprint ?? baseScene?.png_footprint,
      metadata: rawScene?.metadata ?? baseScene?.metadata,
      metadata_tooltip:
        rawScene?.metadata_tooltip ?? baseScene?.metadata_tooltip,
      thumbnailUrl: rawScene?.thumbnailUrl ?? baseScene?.thumbnailUrl,
      previewUrl: rawScene?.previewUrl ?? baseScene?.previewUrl,
      downloadUrl: rawScene?.downloadUrl ?? baseScene?.downloadUrl,
      imageType:
        rawScene?.imageType ?? baseScene?.imageType ?? this.host.imageType,
      operatorKey: rawScene?.operatorKey ?? baseScene?.operatorKey ?? sensor?.operatorKey,
      status: rawScene?.status ?? baseScene?.status,
      skipPreview: rawScene?.skipPreview ?? baseScene?.skipPreview,
    };
  }

  private resolveChangeDetectionSceneTarget(
    sensor: any,
    collection: any,
    sceneId: string,
    sceneData?: any
  ): { scene: any; sensorId: any } | null {
    const candidateSensors = Array.isArray(collection?.sensors)
      ? collection.sensors
      : [];
    const sceneSensorId = sceneData?.sensorId ?? sceneData?.sensor?.id;
    const sensors = [sensor, ...candidateSensors].filter(
      (currentSensor: any, index: number, list: any[]) =>
        !!currentSensor &&
        list.findIndex((entry: any) => entry?.id === currentSensor?.id) ===
          index
    );

    const prioritizedSensors = sceneSensorId
      ? [
          ...sensors.filter(
            (currentSensor: any) =>
              `${currentSensor?.id ?? ''}` === `${sceneSensorId}`
          ),
          ...sensors.filter(
            (currentSensor: any) =>
              `${currentSensor?.id ?? ''}` !== `${sceneSensorId}`
          ),
        ]
      : sensors;

    if (sceneData) {
      for (const currentSensor of prioritizedSensors) {
        const scenes = Array.isArray(currentSensor?.scenes)
          ? currentSensor.scenes
          : [];
        const baseScene = scenes.find(
          (currentScene: any) =>
            `${currentScene?.id ?? ''}` === `${sceneId}` ||
            `${currentScene?.scene_id ?? ''}` === `${sceneId}`
        );
        const scene = this.normalizeChangeDetectionScene(
          sceneData,
          baseScene,
          currentSensor
        );

        if (scene && (baseScene || scene?.footprint)) {
          return {
            scene,
            sensorId: currentSensor?.id ?? sceneSensorId,
          };
        }
      }

      const scene = this.normalizeChangeDetectionScene(
        sceneData,
        undefined,
        sensor
      );
      if (scene?.footprint) {
        return {
          scene,
          sensorId: sceneSensorId ?? sensor?.id ?? collection?.sensors?.[0]?.id,
        };
      }
    }

    for (const currentSensor of prioritizedSensors) {
      const scenes = Array.isArray(currentSensor?.scenes)
        ? currentSensor.scenes
        : [];
      const scene = scenes.find(
        (currentScene: any) =>
          `${currentScene?.id ?? ''}` === `${sceneId}` ||
          `${currentScene?.scene_id ?? ''}` === `${sceneId}`
      );

      if (scene) {
        return {
          scene,
          sensorId: currentSensor?.id,
        };
      }
    }

    return null;
  }

  private loadChangeDetectionScenes(
    collection: any,
    sensor: any,
    sceneData: any[],
    sensorType?: string
  ): void {
    sceneData.forEach((item: any) => {
      const sceneId = this.getChangeDetectionSceneId(item);

      if (!sceneId) {
        return;
      }

      this.loadChangeDetectionRowData(collection, sensor, sceneId, item, {
        sensorType,
      });
    });
  }
}
